import React, {
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react";
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import invariant from "tiny-invariant";
import {combine} from "@atlaskit/pragmatic-drag-and-drop/combine";
import {monitorForElements} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {Instruction, ItemMode} from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";

import type {TreeSource} from "./TreeSource";

import {DependencyContext, TreeContext, TreeContextValue} from "./tree-context";

import {ItemContentProps, ITreeItem as TreeItemType} from "./types";
import TreeItemComponent from "./TreeItemComponent";


import './Tree.css';



export interface TreeProps<T extends TreeItemType> {
    className?: string;
    renderingRootId?: string;
    treeSource: TreeSource<T>;
    renderItemContent: (props: ItemContentProps<T>) => ReactNode;
}

export const Tree = observer(<T extends TreeItemType>(
    {
        className,
        treeSource,
        renderingRootId,
        renderItemContent
    }: TreeProps<T>
) => {
    const ref = useRef<HTMLDivElement>(null);
    const { extractInstruction } = useContext(DependencyContext);

    const treeTopLevelItems = treeSource.getTopLevelItems(renderingRootId)


    const context = useMemo<TreeContextValue>(
        () => ({
            dispatch: treeSource.processEvent,
            treeSource,
            getPathToItem: treeSource.getPathToItem,
            uniqueContextId: Symbol('Tree-id'),
        }),
        [treeSource, treeSource.processEvent, treeSource.getPathToItem],
    );

    useEffect(() => {
        invariant(ref.current);
        return combine(
            monitorForElements({
                canMonitor: ({ source }) => source.data.uniqueContextId === context.uniqueContextId,
                onDrop(args) {
                    const { location, source } = args;
                    // didn't drop on anything
                    if (!location.current.dropTargets.length) {
                        return;
                    }

                    if (source.data.type === 'tree-item') {
                        const itemId = source.data.id as string;

                        const target = location.current.dropTargets[0];
                        const targetId = target.data.id as string;

                        const instruction: Instruction | null = extractInstruction(target.data);

                        if (instruction !== null) {
                            treeSource.processEvent({
                                type: 'instruction',
                                instruction,
                                itemId,
                                targetId,
                            });
                        }
                    }
                },
            }),
        );
    }, [context, extractInstruction, treeSource, treeSource.processEvent]);

    return (
        <TreeContext.Provider value={context}>
            <div className={
                classNames({
                    "Tree": true,
                    [className!]: className,
                })
            } id="tree" ref={ref}>
                {treeTopLevelItems.map((item, index, array) => {
                    const type: ItemMode = (() => {
                        if (item.childrenIds.length && item.isOpen) {
                            return 'expanded';
                        }

                        if (index === array.length - 1) {
                            return 'last-in-group';
                        }

                        return 'standard';
                    })();

                    return <TreeItemComponent<T>
                        treeSource={treeSource}
                        item={item}
                        level={0}
                        key={item.id}
                        mode={type}
                        index={index}
                        renderItemContent={renderItemContent}
                    />;
                })}
            </div>
        </TreeContext.Provider>
    );
})
